import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Send, Loader2, RefreshCw, BarChart2 } from 'lucide-react';
import toast from 'react-hot-toast';

import CrmAnalysisLayout  from '../../components/crmAnalysis/CrmAnalysisLayout';
import ChatHistoryPanel   from '../../components/crmAnalysis/ChatHistoryPanel';
import MarkdownPreview    from '../../components/crmAnalysis/MarkdownPreview';
import { useCrmAnalysis } from '../../context/CrmAnalysisContext';
import { apiService }     from '../../service/Api';

const PLACEHOLDER_QUERIES = [
  'Which deals are at risk of stalling?',
  'Show me deals with no activity in the last 30 days.',
  'What are the top 5 deals by potential revenue?',
  'Summarise the sentiment from recent calls on enterprise deals.',
];

export default function CrmAnalysis() {
  const { tenantname }               = useParams();
  const { state }                    = useLocation();
  const { selectedTenant, setSelectedTenant } = useCrmAnalysis();

  // ── Tenant resolution ──────────────────────────────────────────────────────
  useEffect(() => {
    if (state?.tenantId && !selectedTenant) {
      setSelectedTenant({
        id:     state.tenantId,
        name:   state.tenantName || tenantname,
        domain: tenantname,
      });
    }
  }, [state, selectedTenant, setSelectedTenant, tenantname]);

  const tenantId = selectedTenant?.id ?? state?.tenantId;

  // ── Thread list ────────────────────────────────────────────────────────────
  const [threads, setThreads]           = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  // { thread, userQuery, botMessage }

  const loadThreads = useCallback(async () => {
    if (!tenantId) return;
    setThreadsLoading(true);
    try {
      const res     = await apiService.getCrmAnalysisThreads(tenantId);
      const rawThreads = res.data?.data ?? [];

      // For each thread, pre-fetch messages to extract userQuery so we can
      // show a meaningful label in the sidebar
      const enriched = await Promise.all(
        rawThreads.map(async (thread) => {
          try {
            const msgRes  = await apiService.getThreadMessages(thread.id);
            const msgs    = msgRes.data?.data ?? [];
            const userMsg = msgs.find(m => m.source === 'user');
            return { ...thread, userQuery: userMsg?.message ?? null };
          } catch {
            return { ...thread, userQuery: null };
          }
        })
      );

      setThreads(enriched);
    } catch (err) {
      console.error('Failed to load threads', err);
    } finally {
      setThreadsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  // ── Select a thread from the sidebar ──────────────────────────────────────
  const [messagesLoading, setMessagesLoading] = useState(false);

  const handleSelectThread = async (thread) => {
    if (selectedThread?.thread?.id === thread.id) return;
    setMessagesLoading(true);
    try {
      const res  = await apiService.getThreadMessages(thread.id);
      const msgs = res.data?.data ?? [];
      const userMsg = msgs.find(m => m.source === 'user');
      const botMsg  = msgs.find(m => m.source === 'bot');
      setSelectedThread({
        thread,
        userQuery:  userMsg?.message  ?? '',
        botMessage: botMsg?.message   ?? '',
      });
      setQuery('');
    } catch (err) {
      toast.error('Failed to load conversation');
    } finally {
      setMessagesLoading(false);
    }
  };

  // ── Analysis type toggle (basic | pro) ────────────────────────────────────
  const [analysisType, setAnalysisType] = useState('basic');

  // ── New query submission ───────────────────────────────────────────────────
  const [query, setQuery]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef               = useRef(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = query.trim();
    if (!trimmed || submitting) return;
    if (!tenantId) {
      toast.error('Tenant ID missing — navigate here from the admin dashboard.');
      return;
    }

    setSubmitting(true);
    setSelectedThread({ thread: null, userQuery: trimmed, botMessage: null });

    try {
      const res = await apiService.runDealAnalysisQuery(trimmed, tenantId, analysisType);
      const bot = res.data?.response ?? '';
      setSelectedThread({ thread: null, userQuery: trimmed, botMessage: bot });
      setQuery('');
      // Reload sidebar so the new thread appears
      loadThreads();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Analysis failed';
      toast.error(msg);
      setSelectedThread(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const showWelcome  = !selectedThread && !submitting;
  const showLoading  = submitting || messagesLoading;
  const showResult   = selectedThread && !messagesLoading;

  return (
    <CrmAnalysisLayout tenantName={tenantname} tenantId={tenantId}>
      <div className="flex h-full" style={{ height: 'calc(100vh - 52px)' }}>

        {/* ── Left: Chat history ─────────────────────────────────────────── */}
        <ChatHistoryPanel
          threads={threads}
          selectedId={selectedThread?.thread?.id}
          onSelect={handleSelectThread}
          loading={threadsLoading}
        />

        {/* ── Right: Preview + input ──────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-w-0">

          {/* Preview area */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {showWelcome && (
              <WelcomeState
                tenantname={tenantname}
                onSelect={(q) => { setQuery(q); textareaRef.current?.focus(); }}
              />
            )}

            {showLoading && (
              <div className="flex flex-col items-center justify-center h-48 gap-3">
                <Loader2 className="w-7 h-7 animate-spin" style={{ color: '#D7B472' }} />
                <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.55 }}>
                  {submitting ? 'Running analysis…' : 'Loading conversation…'}
                </p>
              </div>
            )}

            {showResult && (
              <div>
                {/* User query pill */}
                <div className="flex justify-end mb-5">
                  <div
                    className="max-w-xl px-4 py-2.5 rounded-2xl rounded-br-sm text-sm"
                    style={{ backgroundColor: '#D7B472', color: '#FFFFFF' }}
                  >
                    {selectedThread.userQuery}
                  </div>
                </div>

                {/* Bot response */}
                <div
                  className="rounded-xl border p-5"
                  style={{ borderColor: 'rgba(215,180,114,0.35)', backgroundColor: '#FFFFFF' }}
                >
                  {selectedThread.botMessage ? (
                    <MarkdownPreview content={selectedThread.botMessage} />
                  ) : (
                    <div className="flex items-center gap-2 py-4">
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#D7B472' }} />
                      <span className="text-sm" style={{ color: '#0A0A10', opacity: 0.55 }}>
                        Generating response…
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Input bar ─────────────────────────────────────────────────── */}
          <div
            className="flex-shrink-0 border-t px-5 py-4"
            style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF' }}
          >
            {/* Basic / Pro toggle */}
            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-xs font-medium mr-1" style={{ color: '#0A0A10', opacity: 0.45 }}>
                Mode
              </span>
              {['basic', 'pro'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setAnalysisType(t)}
                  className="px-3 py-1 rounded-lg text-xs font-semibold transition-all"
                  style={
                    analysisType === t
                      ? { backgroundColor: '#D7B472', color: '#FFFFFF' }
                      : { backgroundColor: 'rgba(215,180,114,0.1)', color: '#8B6914' }
                  }
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
              <span className="text-xs ml-1" style={{ color: '#0A0A10', opacity: 0.35 }}>
                {analysisType === 'basic'
                  ? '— deterministic scoring, no per-deal LLM'
                  : '— full LLM analysis per deal'}
              </span>
            </div>

            <form onSubmit={handleSubmit} className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Ask a question about your deals… (Shift+Enter for new line)"
                  disabled={submitting || !tenantId}
                  className="w-full resize-none rounded-xl border px-4 py-3 text-sm outline-none transition-all disabled:opacity-50"
                  style={{
                    borderColor: '#D7B472',
                    color: '#0A0A10',
                    backgroundColor: '#FDFCF9',
                    lineHeight: '1.6',
                  }}
                />
              </div>
              <div className="flex flex-col gap-2">
                <button
                  type="submit"
                  disabled={!query.trim() || submitting || !tenantId}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#D7B472', color: '#FFFFFF' }}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">
                    {submitting ? 'Running…' : 'Analyse'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={loadThreads}
                  title="Refresh history"
                  className="flex items-center justify-center p-2.5 rounded-xl border transition-colors hover:bg-gray-50"
                  style={{ borderColor: '#D7B472', color: '#8B6914' }}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </CrmAnalysisLayout>
  );
}

// ── Welcome state ────────────────────────────────────────────────────────────
function WelcomeState({ tenantname, onSelect }) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-64 gap-6 py-12">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: 'rgba(215,180,114,0.15)' }}
      >
        <BarChart2 className="w-7 h-7" style={{ color: '#D7B472' }} />
      </div>
      <div className="text-center">
        <h2 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>
          Deal Analysis
        </h2>
        <p className="text-sm mt-1 max-w-sm" style={{ color: '#0A0A10', opacity: 0.55 }}>
          Ask a question about <strong>{tenantname}</strong>'s deals and get an
          AI-powered analysis pulled from their CRM data.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
        {PLACEHOLDER_QUERIES.map((q) => (
          <button
            key={q}
            onClick={() => onSelect(q)}
            className="text-left text-xs px-4 py-3 rounded-xl border transition-all hover:border-yellow-400"
            style={{
              borderColor: 'rgba(215,180,114,0.4)',
              backgroundColor: '#FFFFFF',
              color: '#0A0A10',
            }}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}
